'use client';

import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import { createContext, useContext, type ReactNode } from 'react';
import { searchEngineDefinition } from './engineDefinition';
import { listingEngineDefinition } from './listingEngineDefinition';

/**
 * Which engine definition backs the current results page.
 *
 * `defineSearchEngine` builds one React context (and therefore one set of
 * generated controller hooks + StateProvider) per call, so the search and
 * listing definitions are not interchangeable: a hook from the search
 * definition reads nothing inside the listing definition's StateProvider. The
 * shared `ssr/` components must therefore reach the hooks of whichever
 * definition their page mounted, which is what this context carries.
 *
 * INVARIANT: the definition is fixed for the lifetime of a page. Components
 * call hooks they obtain from this context — normally a lint violation, since a
 * changing value would change the hook order — and that is safe only because
 * the provider value never changes under a mounted tree: each route mounts one
 * surface's definition (SearchEngineProvider resolves it from the server-passed
 * hub) and navigating to the other surface unmounts the whole tree. Anything
 * that would swap the definition in place must remount instead.
 */
export type SurfaceEngineDefinition = typeof searchEngineDefinition;

const DEFINITIONS_BY_HUB: Record<CoveoSearchHub, SurfaceEngineDefinition> = {
  [CoveoSearchHub.SEARCH]: searchEngineDefinition,
  [CoveoSearchHub.LISTING]: listingEngineDefinition,
};

/**
 * The client-side singleton definition for a surface. Resolved from the hub
 * because that is what a Server Component can hand a Client Component — a
 * definition object carries functions and cannot cross the boundary.
 */
export const getSurfaceEngineDefinition = (
  hub: CoveoSearchHub
): SurfaceEngineDefinition => DEFINITIONS_BY_HUB[hub];

/**
 * Defaults to the search definition so a component rendered without a provider
 * (Storybook stories mount `searchEngineDefinition.StateProvider` directly)
 * still resolves to a real definition. Every page mounts the provider.
 */
const SearchDefinitionContext = createContext<SurfaceEngineDefinition>(
  searchEngineDefinition
);

export const SearchDefinitionProvider = ({
  definition,
  children,
}: {
  definition: SurfaceEngineDefinition;
  children: ReactNode;
}) => (
  <SearchDefinitionContext.Provider value={definition}>
    {children}
  </SearchDefinitionContext.Provider>
);

export const useSearchDefinition = () => useContext(SearchDefinitionContext);

/** The active definition's generated controller hooks. */
export const useSearchControllers = () => useSearchDefinition().controllers;

/** The active definition's built engine, or undefined before hydration. */
export const useSearchEngine = () => useSearchDefinition().useEngine();
